import type { ClubInformationsSchema } from "@teamz/common";

import EditIcon from "@/assets/edit.svg";
import HeartIcon from "@/assets/heart.svg";
import MsgIcon from "@/assets/msg-none.svg";
import ShareIcon from "@/assets/share.svg";
import { useUser } from "@clerk/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubCountryCodes, clubInformationsSchema } from "@teamz/common";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useOpenMessageThread } from "@/hooks/use-open-message-thread";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn, fileToURL, uploadClerkProfileImage } from "@/lib/utils";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { ErrorField, RootField, TextAreaField, TextField } from "../ui/text-field";

function ClubInformationsForm({ club, closeModal }: { club: RouterOutputs["club"]["getClub"]; closeModal: () => void }) {
  const [profileImageFile, setProfileImageFile] = useState<FileList | null>(null);
  const { user } = useMe();
  const { user: clerkUser } = useUser();
  const [clerkProfileImageError, setClerkProfileImageError] = useState<string>();
  const [descriptionLength, setDescriptionLength] = useState(club.clubDescription.length);
  const { getCountryName } = useTranslationUtils();
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.updateProfile.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const clubInformationsForm = useForm<ClubInformationsSchema>({
    resolver: zodResolver(clubInformationsSchema),
    defaultValues: club,
  });

  const onSubmit = clubInformationsForm.handleSubmit(async (data) => {
    const profileImgFile = profileImageFile?.[0];

    // Garde en profondeur : l'upload Clerk cible toujours le compte connecté,
    // jamais celui d'un club tiers édité par un admin.
    if (!profileImgFile || user.id !== club.clubRepresentativeId) {
      return mutate({ clubInfos: { ...data, clubRepresentativeId: club.clubRepresentativeId } });
    }

    const clerkRes = await uploadClerkProfileImage(profileImgFile, clerkUser);

    if (clerkRes.status === "error") {
      setClerkProfileImageError(clerkRes.errorMsg);
      return;
    }

    mutate({ clubInfos: { ...data, imgUrl: clerkRes.publicUrl, clubRepresentativeId: club.clubRepresentativeId } });
  });

  return (
    <>
      <SheetForm id="ClubInformationsForm" onSubmit={onSubmit}>
        {/* La photo passe par le compte Clerk connecté : un admin qui édite un autre
            club écraserait son propre avatar, on masque donc le champ. */}
        {user.id === club.clubRepresentativeId && (
          <div className="flex w-full items-center gap-4">
            <label className="relative shrink-0 cursor-pointer rounded-2xl bg-neutral-600 p-1.5">
              <img
                src={profileImageFile?.[0] ? fileToURL(profileImageFile[0]) : club.imgUrl}
                alt={`Logo de ${club.clubName}`}
                className="pointer-events-none size-[88px] select-none rounded-xl object-cover"
              />
              <div className="absolute -bottom-2 -right-2 flex size-8 items-center justify-center rounded-full bg-neutral-500 text-neutral-300">
                <EditIcon className="size-5" />
              </div>
              <input
                type="file"
                multiple={false}
                accept="image/jpeg,image/png,image/jpg"
                onChange={e => setProfileImageFile(e.target.files)}
                className="sr-only"
              />
            </label>
            <div className="flex flex-col gap-1">
              <Label>{t("profile:club-photo")}</Label>
              {clerkProfileImageError && <ErrorField>{clerkProfileImageError}</ErrorField>}
            </div>
          </div>
        )}

        <TextField
          label={t("common:club-name")}
          {...clubInformationsForm.register("clubName")}
          errorMessage={tDyn(t, clubInformationsForm.formState.errors.clubName?.message)}
        />

        <TextField
          label={t("common:city")}
          {...clubInformationsForm.register("clubCity")}
          errorMessage={tDyn(t, clubInformationsForm.formState.errors.clubCity?.message)}
        />

        <Controller
          control={clubInformationsForm.control}
          name="clubCountryCode"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("common:country")}</Label>
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectGroup>
                    {clubCountryCodes.map(country => (
                      <SelectItem key={country} value={country}>
                        {getCountryName(country)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        <TextAreaField
          label={(
            <span className="inline-flex w-full justify-between child:block">
              <span>Description</span>
              <span className={cn("text-neutral-450", descriptionLength > 730 && "text-orange-300", descriptionLength === 750 && "text-orange-500")}>
                {descriptionLength}
                /750
              </span>
            </span>
          )}
          errorMessage={tDyn(t, clubInformationsForm.formState.errors.clubDescription?.message)}
          {...clubInformationsForm.register("clubDescription", {
            onChange(event) {
              if (event.target.value.trim().length > 750) {
                event.target.value = event.target.value.trim().slice(0, 750);
              }
              if (event.target.value.trim().length === 0) {
                event.target.value = "";
              }
              setDescriptionLength(event.target.value.length);
            },
          })}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="ClubInformationsForm" loading={isPending || clubInformationsForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function ClubInformations({ club }: { club: RouterOutputs["club"]["getClub"] }) {
  const { user } = useMe();
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { t } = useTranslation(["common"]);
  const { getCountryName } = useTranslationUtils();
  const openMessageThread = useOpenMessageThread();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data } = useQuery(trpc.player.getIsClubFavorite.queryOptions(
    { clubId: club.id },
    { enabled: user.role === "player" },
  ));

  const { mutate } = useMutation(trpc.player.updateFavoritesClubs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getIsClubFavorite.queryFilter());
    },
  }));

  const updateFavorite = () => {
    mutate({
      clubId: club.id,
    });
  };

  function upsertThread() {
    openMessageThread(club.clubRepresentativeId);
  }

  return (
    <div className="flex flex-col items-center gap-4 md:items-start">
      <img
        src={club.imgUrl}
        alt={`Profil de ${club.clubRepresentativeFirstName}`}
        className="pointer-events-none z-20 mx-2 -mt-16 size-[88px] select-none rounded-xl border-4 border-white bg-white object-cover"
      />

      <div className="flex flex-col items-center gap-2 px-2 md:items-start">
        <h1 className="text-balance text-center text-2xl font-semibold md:text-wrap md:text-start">
          {club.clubName}
        </h1>
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {club.clubCity}
          </span>
          <div className="flex w-auto max-w-48 items-center gap-2 rounded-full bg-neutral-100 p-0.5">
            <CircleFlag countryCode={club.clubCountryCode.toLowerCase()} className="size-5 rounded-full" />
            <span className="truncate pr-3 text-sm">
              {getCountryName(club.clubCountryCode)}
            </span>
          </div>
        </div>
      </div>

      {(user.role !== "player" || club.clubDescription.length > 0) && (
        <p className="whitespace-pre-line px-2 text-sm text-neutral-600">
          {club.clubDescription.length > 0 ? club.clubDescription : t("common:no-description")}
        </p>
      )}

      <div className="flex w-full flex-1 items-end gap-2">
        { user.role === "club" || user.isAdmin
          ? (
              <>
                <Sheet open={modalState === "open"} onOpenChange={isOpen => updateModalState(isOpen ? "open" : "close")}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <EditIcon />
                      {t("common:edit")}
                    </Button>
                  </SheetTrigger>
                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>Profil club</SheetTitle>
                    </SheetHeader>

                    <ClubInformationsForm club={club} closeModal={() => updateModalState("close")} />
                  </SheetContent>
                </Sheet>

                <Button variant="outline" className="w-full" disabled>
                  <ShareIcon />
                  {t("common:share")}
                </Button>
              </>
            )
          : (
              <>
                <Button
                  variant="outline"
                  onClick={updateFavorite}
                  className="w-full active:border-neutral-300"
                >
                  <HeartIcon
                    className={
                      data?.isFavorite
                        ? "fill-[#FE5000] "
                        : "fill-white "
                    }
                  />
                  {t("common:mobile-nav.favorites")}
                </Button>
                <Button variant="outline" className="w-full" onClick={upsertThread}>
                  <MsgIcon />
                  {t("common:msg")}
                </Button>
              </>
            )}
      </div>
    </div>
  );
}
